import React, {createContext, useContext, useState, useEffect} from "react";
import * as axios from "axios";

const STORAGE_KEY = 'selected_main_city';
const TURDA_CAMPIA_VALUE = 'turda-campia';
const TURDA_CAMPIA_NAMES = ['Turda', 'Câmpia Turzii'];

// Turda and Câmpia Turzii are each their own "main" city in the backend
// (ids 1 and 2 in the cities table) but have always been sold as a single
// delivery area, so they're merged into one dropdown option here instead of
// being listed separately. Every other main city gets its own option, each
// carrying the real city id(s) it maps to so callers never have to resolve
// the dropdown's string value themselves.
function buildCityOptions(cities) {
  const grouped = cities.filter((city) => TURDA_CAMPIA_NAMES.includes(city.name));
  const rest = cities.filter((city) => !TURDA_CAMPIA_NAMES.includes(city.name));
  const options = [];
  if (grouped.length > 0) {
    options.push({value: TURDA_CAMPIA_VALUE, label: 'Turda / Câmpia Turzii', cityIds: grouped.map((c) => c.id)});
  }
  rest
    .sort((a, b) => a.name.localeCompare(b.name, 'ro'))
    .forEach((city) => options.push({value: String(city.id), label: city.name, cityIds: [city.id]}));
  return options;
}

const CityContext = createContext({
  cityOptions: [],
  selectedCityValue: TURDA_CAMPIA_VALUE,
  selectedCityIds: [],
  setSelectedCityValue: () => {},
});

// Single source of truth for "which city's data is the admin looking at" —
// backs the sidebar dropdown (Components/Navbar/CitySelect.js) and anything
// that needs to filter its own data by the selection (starting with the
// dashboard, Components/Orders.js). Persisted per-browser via localStorage,
// same pattern as DarkModeContext, so a reload keeps the last choice.
export function CityProvider({children}) {
  const [cities, setCities] = useState([]);
  const [selectedCityValue, setSelectedCityValueState] = useState(
    () => {
      try {
        return localStorage.getItem(STORAGE_KEY) || TURDA_CAMPIA_VALUE;
      } catch (e) {
        return TURDA_CAMPIA_VALUE;
      }
    }
  );

  useEffect(() => {
    const getCities = async () => {
      try {
        const response = await axios.default.get('https://api.appetito.ro/v1/city');
        setCities(response.data || []);
      } catch (e) {
        setCities([]);
      }
    };
    getCities();
  }, []);

  const cityOptions = buildCityOptions(cities);
  const activeOption = cityOptions.find((option) => option.value === selectedCityValue) || cityOptions[0];
  const selectedCityIds = activeOption ? activeOption.cityIds : [];

  const setSelectedCityValue = (value) => {
    setSelectedCityValueState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // ignore (e.g. private browsing with storage blocked)
    }
  };

  return (
    <CityContext.Provider value={{cityOptions, selectedCityValue, selectedCityIds, setSelectedCityValue}}>
      {children}
    </CityContext.Provider>
  );
}

export const useCity = () => useContext(CityContext);
